import { type Group, type Object3D, DoubleSide } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import type { SceneObject } from '../../../types/engine.types';
import type { ModelInfo } from '../../../../model/model-info';
import { applyPlaceholderTint } from '../../../managers/model-manager/helpers';
import { IV4 } from '../../../../interfaces/iv-4';
import { normalizeSurroundKey, normalizeCountertopKey, shouldRenderApplianceModel } from '../helpers';

// WOOD / LOG-INSERT is a flat arch mesh that sits against a panel face. Its outward placement is
// already handled by the standard model transform (panel-local position rotated by the panel angle,
// plus the catalog `modelOffset.z`) — the same path every other appliance uses, so it is correct for
// any island depth, panel thickness and panel angle. A hardcoded world-space nudge here only duplicated
// that offset with the wrong normal sign (`z += cos` instead of the true outward `z -= cos`), which
// buried the mesh on angled corner-island panels. So we do NOT touch position; we only force DoubleSide
// + polygon offset so the flat coplanar mesh renders both faces without z-fighting against the panel.
const applyLogInsertSurface = (model: Object3D): void => {
	model.traverse((child: any) => {
		if (child.isMesh && child.material) {
			const mats = Array.isArray(child.material) ? child.material : [child.material];
			for (const mat of mats) {
				mat.side = DoubleSide;
				mat.polygonOffset = true;
				mat.polygonOffsetFactor = -1;
				mat.polygonOffsetUnits = -1;
				mat.needsUpdate = true;
			}
		}
	});
};

/**
 * Load appliance models
 */
export const loadAppliancesCfn = async (
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): Promise<void> => {
	if (!self.configAccess.modelManager) return;

	const islands = self.kitchenDataManagerAccess.getIslands();
	const modelInfos: ModelInfo[] = [];

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];

		if (island.panels) {
			for (const panel of island.panels) {
				const panelCenter = panel.center || { x: 0, y: 0, z: 0 };
				// originAngle is stored in radians from KitchenDataManager
				const rotationZ = panel.originAngle || 0;

				if (panel.applianceGroups) {
					for (const applianceGroup of panel.applianceGroups) {
						const agCenter = applianceGroup.center || { x: 0, y: 0, z: 0 };
						const groupId = applianceGroup.id || null;

						if (applianceGroup.items) {
							for (const item of applianceGroup.items) {
								// Spacer items reserve space but have no 3D model
								if (item.isSpacer) continue;

								// Calculate model position relative to panel center
								// Matches Haxe LoadModelsCommand.hx
								const modelX = agCenter.x + (item.center?.x || 0) - ((item.size?.width || 0) / 2) - (item.modelOffset?.x || 0);
								const modelY = agCenter.y + (item.center?.y || 0) + ((item.size?.height || 0) / 2) - (item.modelOffset?.y || 0);
								const modelZ = agCenter.z + (item.center?.z || 0) + ((item.size?.depth || 0) / 2) + (item.modelOffset?.z || 0);
								const modelRotation = item.modelRotation || { x: 0, y: 0, z: 0 };

								// Transform position based on panel rotation
								const position: IV4 = {
									x: panelCenter.x + (Math.cos(rotationZ) * modelX) + (Math.sin(rotationZ) * modelZ),
									y: modelY,
									z: -panelCenter.z - (Math.cos(rotationZ) * modelZ) + (Math.sin(rotationZ) * modelX),
									w: 0
								};

								const { ModelInfo: ModelInfoClass } = await import('../../../../model/model-info');
								const modelInfoInstance = new ModelInfoClass({
									id: item.id || `model-${islandIndex}-${modelInfos.length}`,
									sku: item.sku || '',
									position,
									islandPositionIndex: islandIndex,
									h: item.size?.height || 0,
									w: item.size?.width || 0,
									z: item.size?.depth || 0,
									rotationZ, // Now in radians
									holeType: null,
									modelDepth: null,
									renderAsBarIsland: null,
									islandType: island.id || '',
									placement: item.placement || null
								});
								// Compute cutout 3D position (same formula as generate-cutouts)
								const cutoutLocalX = agCenter.x;
								const cutoutLocalZ = item.cutoutCenter?.z || 0;
								const cutoutPos3D = {
									x: panelCenter.x + (Math.cos(rotationZ) * cutoutLocalX) + (Math.sin(rotationZ) * cutoutLocalZ),
									y: agCenter.y + (item.cutoutCenter?.y || 0),
									z: -panelCenter.z - (Math.cos(rotationZ) * cutoutLocalZ) + (Math.sin(rotationZ) * cutoutLocalX)
								};

								(modelInfoInstance as any).panelItemGroupId = groupId;
								(modelInfoInstance as any).modelRotation = modelRotation;
								(modelInfoInstance as any).instanceRotation = item.instanceRotation || { x: 0, y: 0, z: 0 };
								(modelInfoInstance as any).cutoutPos3D = cutoutPos3D;
								(modelInfoInstance as any).distanceBelowCountertop = item.distanceBelowCountertop || 0;
								(modelInfoInstance as any).categoryKey = item.categoryKey || null;
								(modelInfoInstance as any).placeholderModelSku = item.placeholderModelSku ?? null;
								(modelInfoInstance as any).modelFile = item.modelFile ?? null;
								modelInfos.push(modelInfoInstance);
							}
						}
					}
				}
			}
		}
	}

	if (modelInfos.length > 0) {
		const counterMaterialAlias = normalizeCountertopKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.countertop?.key);
		const surroundMaterialAlias = normalizeSurroundKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.surround?.key);

		const generation = self.configAccess.getBuildGeneration?.() ?? 0;
		const noModelSkus = input.controller?.noModelSkus ?? [];
		const filteredModelInfos = modelInfos.filter((info) => shouldRenderApplianceModel(info, noModelSkus));

		// Build model load options from ModelInfo objects
		// Note: rotation.z matches legacy dae.rotation.z = model.rotationZ
		const modelLoadOptions = filteredModelInfos.map(info => {
			const placeholderSku = (info as any).placeholderModelSku as string | null | undefined;
			const effectiveSku = placeholderSku || info.sku;
			// Special handling for LOG-INSERT - use WOOD.dae instead
			const modelFile = effectiveSku === 'LOG-INSERT' ? 'WOOD.dae' : ((info as any).modelFile || `${effectiveSku}.dae`);
			const mr = (info as any).modelRotation || { x: 0, y: 0, z: 0 };
			const ir = (info as any).instanceRotation || { x: 0, y: 0, z: 0 };
			const isPlaceholder = Boolean(placeholderSku);

			return {
				path: `models/${modelFile}`,
				position: {
					x: info.position.x,
					y: info.position.y,
					z: info.position.z
				},
				rotation: {
					x: mr.x || 0,
					y: mr.y || 0,
					z: (info.rotationZ ?? 0) + (mr.z || 0)
				},
				metadata: {
					id: info.id,
					type: 'appliance' as const,
					sku: info.sku,
					islandPositionIndex: info.islandPositionIndex,
					placement: info.placement,
					depth: info.zpos,
					isLogInsert: effectiveSku === 'LOG-INSERT' || effectiveSku === 'WOOD',
					rotationZ: info.rotationZ ?? 0,
					modelRotation: { x: mr.x || 0, y: mr.y || 0, z: mr.z || 0 },
					instanceRotation: { x: ir.x || 0, y: ir.y || 0, z: ir.z || 0 },
					cutoutPos3D: (info as any).cutoutPos3D || null,
					hasCutout: !!(info as any).distanceBelowCountertop && (info as any).distanceBelowCountertop > 0,
					isPlaceholder
				}
			};
		});

		const loadedModels = await self.configAccess.modelManager.loadModels(modelLoadOptions, {}, { strict: true });

		if (self.configAccess.getBuildGeneration?.() !== generation) return;

		// Models that 404 permanently render as absent — prune them from the manifest so readiness and GLB validation stay consistent
		const missingSkus = self.configAccess.modelManager.missingModelSkusAccess;
		if (missingSkus.size > 0 && self.currentManifestAccess) {
			self.currentManifestAccess.appliances = self.currentManifestAccess.appliances.filter((appliance) => !missingSkus.has(appliance.sku));
		}

		if (loadedModels.length === 0) {
			console.warn('KitchenSceneBuilder.loadAppliances: No models loaded - appliances will be missing');
		}

		// Add models to appropriate island groups and apply post-load adjustments
		for (const model of loadedModels) {
			if (model.userData?.isLogInsert) {
				applyLogInsertSurface(model);
			}

			// Placeholder appliances: tint only. Size/placement come from the placeholder's
			// catalog component (substituted upstream in KitchenDataManager), so the model
			// loads at native dimensions and the cutout geometry naturally aligns with it.
			if (model.userData?.isPlaceholder) {
				applyPlaceholderTint(model);
			}

			const islandIndex = model.userData?.islandPositionIndex ?? 0;
			const islandGroup = self.islandGroupsAccess[islandIndex];
			if (islandGroup) {
				islandGroup.add(model);
				// Ensure model is visible and opacity restored (in case of reused materials)
				// Skip Image-arch materials which are intentionally transparent (WOOD arch)
				model.traverse((child: any) => {
					if (child.material) {
						const mats = Array.isArray(child.material) ? child.material : [child.material];
						for (const mat of mats) {
							if (mat.name === 'Image-arch') continue;
							mat.opacity = mat.opacity ?? 1;
							mat.transparent = mat.transparent ?? false;
						}
					}
				});
			}
		}
	} else {
		console.warn('KitchenSceneBuilder.loadAppliances: No appliance model infos generated');
	}
};

/**
 * Load appliance models for a single island by index.
 * Per-island version of loadAppliancesCfn for incremental island rebuilds.
 */
export const loadAppliancesForIslandCfn = async (
	self: KitchenSceneBuilder,
	{ input, islandIndex, targetGroup }: { input: KitchenBlueprintInput; islandIndex: number; targetGroup?: Group }
): Promise<void> => {
	if (!self.configAccess.modelManager) return;

	const islands = self.kitchenDataManagerAccess.getIslands();
	const island = islands[islandIndex];
	if (!island) return;

	const islandGroup = targetGroup ?? self.islandGroupsAccess[islandIndex];
	if (!islandGroup) return;

	const modelInfos: ModelInfo[] = [];

	if (island.panels) {
		for (const panel of island.panels) {
			const panelCenter = panel.center || { x: 0, y: 0, z: 0 };
			const rotationZ = panel.originAngle || 0;

			if (panel.applianceGroups) {
				for (const applianceGroup of panel.applianceGroups) {
					const agCenter = applianceGroup.center || { x: 0, y: 0, z: 0 };
					const groupId = applianceGroup.id || null;

					if (applianceGroup.items) {
						for (const item of applianceGroup.items) {
							if (item.isSpacer) continue;

							const modelX = agCenter.x + (item.center?.x || 0) - ((item.size?.width || 0) / 2) - (item.modelOffset?.x || 0);
							const modelY = agCenter.y + (item.center?.y || 0) + ((item.size?.height || 0) / 2) - (item.modelOffset?.y || 0);
							const modelZ = agCenter.z + (item.center?.z || 0) + ((item.size?.depth || 0) / 2) + (item.modelOffset?.z || 0);
							const modelRotation = item.modelRotation || { x: 0, y: 0, z: 0 };

							const position: IV4 = {
								x: panelCenter.x + (Math.cos(rotationZ) * modelX) + (Math.sin(rotationZ) * modelZ),
								y: modelY,
								z: -panelCenter.z - (Math.cos(rotationZ) * modelZ) + (Math.sin(rotationZ) * modelX),
								w: 0
							};

							// Compute cutout 3D position (same formula as generate-cutouts)
							const cutoutLocalX = agCenter.x;
							const cutoutLocalZ = item.cutoutCenter?.z || 0;
							const cutoutPos3D = {
								x: panelCenter.x + (Math.cos(rotationZ) * cutoutLocalX) + (Math.sin(rotationZ) * cutoutLocalZ),
								y: agCenter.y + (item.cutoutCenter?.y || 0),
								z: -panelCenter.z - (Math.cos(rotationZ) * cutoutLocalZ) + (Math.sin(rotationZ) * cutoutLocalX)
							};

							const { ModelInfo: ModelInfoClass } = await import('../../../../model/model-info');
							const modelInfoInstance = new ModelInfoClass({
								id: item.id || `model-${islandIndex}-${modelInfos.length}`,
								sku: item.sku || '',
								position,
								islandPositionIndex: islandIndex,
								h: item.size?.height || 0,
								w: item.size?.width || 0,
								z: item.size?.depth || 0,
								rotationZ,
								holeType: null,
								modelDepth: null,
								renderAsBarIsland: null,
								islandType: island.id || '',
								placement: item.placement || null
							});
							(modelInfoInstance as any).panelItemGroupId = groupId;
							(modelInfoInstance as any).modelRotation = modelRotation;
							(modelInfoInstance as any).instanceRotation = item.instanceRotation || { x: 0, y: 0, z: 0 };
							(modelInfoInstance as any).cutoutPos3D = cutoutPos3D;
							(modelInfoInstance as any).distanceBelowCountertop = item.distanceBelowCountertop || 0;
							(modelInfoInstance as any).categoryKey = item.categoryKey || null;
							(modelInfoInstance as any).placeholderModelSku = item.placeholderModelSku ?? null;
							(modelInfoInstance as any).modelFile = item.modelFile ?? null;
							modelInfos.push(modelInfoInstance);
						}
					}
				}
			}
		}
	}

	if (modelInfos.length === 0) return;

	const counterMaterialAlias = normalizeCountertopKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.countertop?.key);
	const surroundMaterialAlias = normalizeSurroundKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.surround?.key);

	const generation = self.configAccess.getBuildGeneration?.() ?? 0;
	const noModelSkus = input.controller?.noModelSkus ?? [];
	const filteredModelInfos = modelInfos.filter((info) => shouldRenderApplianceModel(info, noModelSkus));

	const modelLoadOptions = filteredModelInfos.map(info => {
		const placeholderSku = (info as any).placeholderModelSku as string | null | undefined;
		const effectiveSku = placeholderSku || info.sku;
		const modelFile = effectiveSku === 'LOG-INSERT' ? 'WOOD.dae' : ((info as any).modelFile || `${effectiveSku}.dae`);
		const mr = (info as any).modelRotation || { x: 0, y: 0, z: 0 };
		const ir = (info as any).instanceRotation || { x: 0, y: 0, z: 0 };
		const isPlaceholder = Boolean(placeholderSku);

		return {
			path: `models/${modelFile}`,
			position: {
				x: info.position.x,
				y: info.position.y,
				z: info.position.z
			},
			rotation: {
				x: mr.x || 0,
				y: mr.y || 0,
				z: (info.rotationZ ?? 0) + (mr.z || 0)
			},
			metadata: {
				id: info.id,
				type: 'appliance' as const,
				sku: info.sku,
				islandPositionIndex: info.islandPositionIndex,
				placement: info.placement,
				depth: info.zpos,
				isLogInsert: effectiveSku === 'LOG-INSERT' || effectiveSku === 'WOOD',
				rotationZ: (info as any).rotationZ ?? 0,
				modelRotation: { x: mr.x || 0, y: mr.y || 0, z: mr.z || 0 },
				instanceRotation: { x: ir.x || 0, y: ir.y || 0, z: ir.z || 0 },
				cutoutPos3D: (info as any).cutoutPos3D || null,
				hasCutout: !!(info as any).distanceBelowCountertop && (info as any).distanceBelowCountertop > 0,
				isPlaceholder
			}
		};
	});

	const loadedModels = await self.configAccess.modelManager.loadModels(modelLoadOptions, {}, { strict: true });

	if (self.configAccess.getBuildGeneration?.() !== generation) return;

	// Models that 404 permanently render as absent — prune them from the manifest so readiness and GLB validation stay consistent
	const missingSkus = self.configAccess.modelManager.missingModelSkusAccess;
	if (missingSkus.size > 0 && self.currentManifestAccess) {
		self.currentManifestAccess.appliances = self.currentManifestAccess.appliances.filter((appliance) => !missingSkus.has(appliance.sku));
	}

	for (const model of loadedModels) {
		if (model.userData?.isLogInsert) {
			applyLogInsertSurface(model);
		}

		if (model.userData?.isPlaceholder) {
			applyPlaceholderTint(model);
		}

		islandGroup.add(model);
		model.traverse((child: any) => {
			if (child.material) {
				const mats = Array.isArray(child.material) ? child.material : [child.material];
				for (const mat of mats) {
					if (mat.name === 'Image-arch') continue;
					mat.opacity = mat.opacity ?? 1;
					mat.transparent = mat.transparent ?? false;
				}
			}
		});
	}
};

/**
 * Load appliances progressively with type metadata.
 * Yields each appliance individually for type-based animation.
 */
export async function* loadAppliancesProgressiveTypedCfn(
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): AsyncGenerator<SceneObject, void, void> {
	if (!self.configAccess.modelManager) return;

	const islands = self.kitchenDataManagerAccess.getIslands();
	const modelInfos: { info: any; islandGroup: Group; islandCenter?: { x: number; y: number; z: number } }[] = [];

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];
		const islandGroup = self.islandGroupsAccess[islandIndex];
		const islandCenter = islandGroup?.userData?.center;

		if (island.panels) {
			for (const panel of island.panels) {
				const panelCenter = panel.center || { x: 0, y: 0, z: 0 };
				const rotationZ = panel.originAngle || 0;

				if (panel.applianceGroups) {
					for (const applianceGroup of panel.applianceGroups) {
						const agCenter = applianceGroup.center || { x: 0, y: 0, z: 0 };
						const groupId = applianceGroup.id || null;

						if (applianceGroup.items) {
							for (const item of applianceGroup.items) {
								// Spacer items reserve space but have no 3D model
								if (item.isSpacer) continue;

								// Matches Haxe LoadModelsCommand.hx
								const modelX = agCenter.x + (item.center?.x || 0) - ((item.size?.width || 0) / 2) - (item.modelOffset?.x || 0);
								const modelY = agCenter.y + (item.center?.y || 0) + ((item.size?.height || 0) / 2) - (item.modelOffset?.y || 0);
								const modelZ = agCenter.z + (item.center?.z || 0) + ((item.size?.depth || 0) / 2) + (item.modelOffset?.z || 0);
								const modelRotation = item.modelRotation || { x: 0, y: 0, z: 0 };
								const instanceRotation = item.instanceRotation || { x: 0, y: 0, z: 0 };

								const position = {
									x: panelCenter.x + (Math.cos(rotationZ) * modelX) + (Math.sin(rotationZ) * modelZ),
									y: modelY,
									z: -panelCenter.z - (Math.cos(rotationZ) * modelZ) + (Math.sin(rotationZ) * modelX)
								};

								modelInfos.push({
									info: {
										id: item.id || `model-${islandIndex}-${modelInfos.length}`,
										sku: item.sku || '',
										placeholderModelSku: item.placeholderModelSku ?? null,
										modelFile: item.modelFile ?? null,
										position,
										rotationZ,
										modelRotation,
										instanceRotation,
										categoryKey: item.categoryKey || null,
										islandPositionIndex: islandIndex,
										placement: item.placement || null,
										width: item.size?.width || 0,
										height: item.size?.height || 0,
										depth: item.size?.depth || 0,
										isLogInsert: item.sku === 'LOG-INSERT' || item.sku === 'WOOD'
									},
									islandGroup,
									islandCenter
								});
							}
						}
					}
				}
			}
		}
	}

	// Sort modelInfos by render order (using _islandRenderOrder stored from buildProgressive)
	// This ensures appliances are yielded in the same order as their islands
	const orderMap = new Map(self.islandRenderOrderAccess.map((item, i) => [item.index, i]));
	modelInfos.sort((a, b) => {
		const orderA = orderMap.get(a.info.islandPositionIndex) ?? 999;
		const orderB = orderMap.get(b.info.islandPositionIndex) ?? 999;
		return orderA - orderB;
	});

	// Load and yield each appliance individually
	const noModelSkus = input.controller?.noModelSkus ?? [];
	for (const { info, islandGroup, islandCenter } of modelInfos) {
		if (!shouldRenderApplianceModel(info as any, noModelSkus)) continue;

		const placeholderSku = info.placeholderModelSku as string | null | undefined;
		const effectiveSku = placeholderSku || info.sku;
		const modelFile = effectiveSku === 'LOG-INSERT' ? 'WOOD.dae' : (info.modelFile || `${effectiveSku}.dae`);
		const mr = info.modelRotation || { x: 0, y: 0, z: 0 };
		const ir = info.instanceRotation || { x: 0, y: 0, z: 0 };
		const isPlaceholder = Boolean(placeholderSku);

		try {
			const loadedModels = await self.configAccess.modelManager!.loadModels([{
				path: `models/${modelFile}`,
				position: info.position,
				rotation: { x: mr.x || 0, y: mr.y || 0, z: (info.rotationZ ?? 0) + (mr.z || 0) },
				metadata: {
					id: info.id,
					type: 'appliance' as const,
					sku: info.sku,
					islandPositionIndex: info.islandPositionIndex,
					placement: info.placement,
					depth: info.depth,
					isLogInsert: effectiveSku === 'LOG-INSERT' || effectiveSku === 'WOOD',
					panelItemGroupId: info.panelItemGroupId || null,
					modelRotation: { x: mr.x || 0, y: mr.y || 0, z: mr.z || 0 },
					instanceRotation: { x: ir.x || 0, y: ir.y || 0, z: ir.z || 0 },
					isPlaceholder
				}
			}]);

			for (const model of loadedModels) {
				if (model.userData?.isLogInsert) {
					applyLogInsertSurface(model);
				}

				if (model.userData?.isPlaceholder) {
					applyPlaceholderTint(model);
				}

				model.userData.islandId = islandGroup?.userData?.id;
				model.userData.islandCenter = islandCenter;
				model.userData.panelItemGroupId = info.panelItemGroupId || null;
				// Store original scale for animation (Collada models default to scale 10)
				model.userData.originalScale = model.scale.x;

				// Add to island group
				// Animation helper will set opacity=0 synchronously before any render frame
				islandGroup?.add(model);

				yield {
					object: model,
					metadata: {
						id: info.id,
						type: 'appliance',
						source: 'kitchen',
						sku: info.sku,
						subtype: info.placement,
						islandId: islandGroup?.userData?.id,
						islandIndex: info.islandPositionIndex,
						islandCenter,
						originalScale: model.scale.x
					}
				};
			}
		} catch (error) {
			console.error('KitchenSceneBuilder: Error loading appliance model:', info.sku, error);
		}
	}
}
